import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ClipboardList, MessageSquare, Video } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { api } from '@/lib/api-client';
import { TASK_TYPE_LABELS, TASK_STATUS, type TaskType, type TaskStatus } from '@parecer/shared';
import { formatBRL, formatRelativeDate } from '@/lib/utils';

interface TaskItem {
  taskId: string;
  ownerId: string;
  lawyerId?: string;
  type: TaskType;
  status: TaskStatus;
  description: string;
  totalAmount: number;
  deadlineDays?: number;
  createdAt: string;
  updatedAt: string;
}

type Tab = 'mine' | 'marketplace';

export function LawyerTarefas() {
  const [tab, setTab] = useState<Tab>('mine');

  const { data, isLoading } = useQuery({
    queryKey: ['lawyer', 'tasks', tab],
    queryFn: () =>
      api.get<{ items: TaskItem[] }>(
        tab === 'marketplace' ? `/tasks?status=${TASK_STATUS.OPEN}` : '/tasks',
      ),
  });

  const items = data?.items ?? [];

  return (
    <AppShell role="lawyers">
      <div className="max-w-5xl">
        <h1 className="font-serif text-display-lg">Tarefas</h1>
        <p className="text-muted-foreground mt-1">
          Tarefas atribuídas a você + oportunidades abertas no marketplace.
        </p>

        <div className="mt-6 flex gap-2">
          <Button
            size="sm"
            variant={tab === 'mine' ? 'accent' : 'outline'}
            onClick={() => setTab('mine')}
          >
            <ClipboardList className="h-4 w-4 mr-2" />
            Atribuídas a mim
          </Button>
          <Button
            size="sm"
            variant={tab === 'marketplace' ? 'accent' : 'outline'}
            onClick={() => setTab('marketplace')}
          >
            Marketplace
          </Button>
        </div>

        <div className="mt-6 space-y-3">
          {isLoading && <p className="text-sm text-muted-foreground">Carregando…</p>}
          {!isLoading && items.length === 0 && (
            <Card>
              <CardContent className="pt-6 text-sm text-muted-foreground">
                {tab === 'mine'
                  ? 'Nenhuma tarefa atribuída a você ainda. Quando um cliente te escolher (ou aceitar uma oferta sua no marketplace), aparece aqui.'
                  : 'Nenhuma tarefa aberta no marketplace no momento.'}
              </CardContent>
            </Card>
          )}
          {items.map((t) => (
            <Link key={t.taskId} to={`/lawyer/tarefas/${t.taskId}`} className="block focus-ring">
              <Card className="hover:border-accent/40 transition-colors">
                <CardContent className="py-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-medium truncate">{TASK_TYPE_LABELS[t.type]}</h3>
                      <StatusBadge status={t.status} />
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{t.description}</p>
                    <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                      <span>Criada {formatRelativeDate(t.createdAt)}</span>
                      {t.deadlineDays && <span>· prazo {t.deadlineDays} dias</span>}
                    </div>
                  </div>
                  <div className="flex-none text-right">
                    {t.totalAmount > 0 && (
                      <p className="font-serif text-lg">{formatBRL(t.totalAmount)}</p>
                    )}
                    <div className="mt-2 flex justify-end gap-1 text-muted-foreground">
                      <MessageSquare className="h-4 w-4" />
                      <Video className="h-4 w-4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </AppShell>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { variant: 'success' | 'warning' | 'danger' | 'muted'; label: string }> = {
    open: { variant: 'warning', label: 'Aberta' },
    assigned: { variant: 'warning', label: 'Atribuída' },
    in_progress: { variant: 'warning', label: 'Em andamento' },
    delivered: { variant: 'success', label: 'Entregue' },
    completed: { variant: 'success', label: 'Concluída' },
    rejected: { variant: 'muted', label: 'Rejeitada' },
    cancelled: { variant: 'muted', label: 'Cancelada' },
    disputed: { variant: 'danger', label: 'Em disputa' },
  };
  const info = map[status] ?? { variant: 'muted' as const, label: status };
  return <Badge variant={info.variant}>{info.label}</Badge>;
}
